import { AUTHER_CLIENT, getRequestMetadata } from "../index";
import { Empty } from "google-protobuf/google/protobuf/empty_pb";
import { CreateAPIKeyRequest, DeleteAPIKeyRequest } from "../../protos/auther_pb";
import { convertTimestamp } from "../../util";

/**
 * Fetch the list of existing API keys.
 * Uses the `getAPIKeys` RPC and returns a Promise that resolves with the response.
 *
 * @returns {Promise<GetAPIKeysResponse>} - A promise that resolves with the GetAPIKeysResponse.
 */
const getApiKeys = () => {
  const request = new Empty();

  return new Promise((resolve, reject) => {
    AUTHER_CLIENT.getAPIKeys(request, getRequestMetadata(), (err, response) => {
      if (err) {
        reject(err);
      } else {
        const tokens = response["u"][0].map((tokenRes) => {
          return convertApiKeysFromProto(tokenRes);
        });
        resolve(tokens);
      }
    });
  });
};

/**
 * Create a new API key with a given key name.
 * This uses the `createAPIKey` RPC call.
 *
 * @param {string} key_name - The name of the API key to be created.
 * @returns {Promise<CreateAPIKeyResponse>} - A promise that resolves to the CreateAPIKeyResponse.
 */
function createApiKey(key_name) {
  const request = new CreateAPIKeyRequest({ key_name });
  request.setName(key_name);

  return new Promise((resolve, reject) => {
    AUTHER_CLIENT.createAPIKey(request, getRequestMetadata(), (err, response) => {
      if (err) {
        reject(err);
      } else {
        resolve(response["u"][0]);
      }
    });
  });
}

/**
 * Delete an existing API key by its hashed_key.
 * This uses the `deleteAPIKey` RPC call.
 * According to the provided `.proto` snippet, the request only needs the hashed_key.
 * The server uses authentication data from metadata to ensure security.
 *
 * @param {string} hashed_key - The hashed API key to be deleted.
 * @returns {Promise<Empty>} - A promise that resolves when the API key is successfully deleted.
 */
function deleteApiKey({ hashed_key }) {
  const request = new DeleteAPIKeyRequest();
  request.setHashedKey(hashed_key);

  return new Promise((resolve, reject) => {
    AUTHER_CLIENT.deleteAPIKey(request, getRequestMetadata(), (err, response) => {
      if (err) {
        reject(err);
      } else {
        resolve(response);
      }
    });
  });
}

const convertApiKeysFromProto = (tokenProto) => {
  return {
    hashedKey: tokenProto[0],
    name: tokenProto[1],
    key: tokenProto[2],
    expireAt: convertTimestamp(tokenProto[3]?.[0]),
    user: tokenProto[4][4],
    createdAt: convertTimestamp(tokenProto[5]?.[0])
  };
};

export { getApiKeys, createApiKey, deleteApiKey };
